import React, { useEffect, useCallback } from 'react';
import { ScriptConfiguration } from '../../../../types/workflow';

interface ScriptConfigProps {
  configuration: ScriptConfiguration;
  onChange: (config: ScriptConfiguration) => void;
  isReadOnly: boolean;
}

const ScriptConfig: React.FC<ScriptConfigProps> = ({
  configuration,
  onChange,
  isReadOnly,
}) => {
  const updateConfig = useCallback((field: keyof ScriptConfiguration, value: any) => {
    onChange({
      ...configuration,
      [field]: value,
    });
  }, [configuration, onChange]);

  // Sample code templates
  const getCodeTemplate = (language: string) => {
    if (language === 'javascript') {
      return `// JavaScript Workflow Script
// Available objects: context, input, output, logger

// Example: Validate and process workflow data
function processWorkflow() {
  // Access workflow context
  const workflowId = context.workflowId;
  const currentStep = context.currentStep;
  
  // Access input data
  const requestData = input.data;
  
  // Perform validation
  if (!requestData.approver) {
    logger.error('No approver specified');
    return {
      success: false,
      error: 'Approver is required'
    };
  }
  
  // Process the data
  const result = {
    approver: requestData.approver,
    timestamp: new Date().toISOString(),
    status: 'pending_approval'
  };
  
  // Log the action
  logger.info('Approval request created', result);
  
  // Set output for next step
  output.set('approvalRequest', result);
  
  return {
    success: true,
    data: result
  };
}

// Execute the function
processWorkflow();`;
    } else {
      return `# Python Workflow Script
# Available objects: context, input, output, logger

def process_workflow():
    """Process workflow data"""
    
    # Access workflow context
    workflow_id = context.workflow_id
    current_step = context.current_step
    
    # Access input data
    request_data = input.data
    
    # Perform validation
    if not request_data.get('approver'):
        logger.error('No approver specified')
        return {
            'success': False,
            'error': 'Approver is required'
        }
    
    # Process the data
    import datetime
    result = {
        'approver': request_data['approver'],
        'timestamp': datetime.datetime.now().isoformat(),
        'status': 'pending_approval'
    }
    
    # Log the action
    logger.info('Approval request created', result)
    
    # Set output for next step
    output.set('approvalRequest', result)
    
    return {
        'success': True,
        'data': result
    }

# Execute the function
process_workflow()`;
    }
  };

  // Set template when language changes or when code is empty
  useEffect(() => {
    if (!configuration.code && configuration.language) {
      updateConfig('code', getCodeTemplate(configuration.language));
    }
  }, [configuration.code, configuration.language, updateConfig]);

  // Function to handle tab key in textarea
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const target = e.target as HTMLTextAreaElement;
      const start = target.selectionStart;
      const end = target.selectionEnd;
      const value = target.value;
      
      // Insert 2 spaces for tab
      const newValue = value.substring(0, start) + '  ' + value.substring(end);
      updateConfig('code', newValue);
      
      // Reset cursor position after React re-render
      setTimeout(() => {
        target.selectionStart = target.selectionEnd = start + 2;
      }, 0);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Language
        </label>
        <select
          value={configuration.language || 'javascript'}
          onChange={(e) => {
            const newLang = e.target.value as 'javascript' | 'python';
            updateConfig('language', newLang);
            // Optionally update template if code is empty
            if (!configuration.code || configuration.code.trim() === '') {
              updateConfig('code', getCodeTemplate(newLang));
            }
          }}
          disabled={isReadOnly}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
        >
          <option value="javascript">JavaScript</option>
          <option value="python">Python</option>
        </select>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Script Code
          </label>
          <div className="flex gap-2">
            {!configuration.code && (
              <button
                type="button"
                onClick={() => updateConfig('code', getCodeTemplate(configuration.language || 'javascript'))}
                className="text-xs text-primary-600 hover:text-primary-700"
                disabled={isReadOnly}
              >
                Load Template
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                const formatted = configuration.code?.trim() || '';
                updateConfig('code', formatted);
              }}
              className="text-xs text-primary-600 hover:text-primary-700"
              disabled={isReadOnly || !configuration.code}
            >
              Trim Whitespace
            </button>
          </div>
        </div>
        
        <div className="relative border border-gray-300 rounded-md overflow-hidden">
          {/* Line numbers */}
          <div className="absolute left-0 top-0 bottom-0 w-12 bg-gray-50 border-r border-gray-200 overflow-hidden">
            <div className="text-xs text-gray-500 text-right pr-2 pt-3 leading-5 select-none">
              {(configuration.code || '').split('\n').map((_, index) => (
                <div key={index}>{index + 1}</div>
              ))}
            </div>
          </div>
          
          {/* Code editor */}
          <textarea
            value={configuration.code || ''}
            onChange={(e) => updateConfig('code', e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isReadOnly}
            placeholder={`Enter ${configuration.language || 'JavaScript'} code here...`}
            className="w-full h-[400px] pl-14 pr-3 py-3 font-mono text-sm bg-white resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset disabled:bg-gray-100"
            style={{ 
              tabSize: 2,
              lineHeight: '1.25rem',
              fontFamily: 'Consolas, Monaco, "Courier New", monospace'
            }}
            spellCheck={false}
          />
        </div>
        
        <div className="mt-1 flex justify-between text-xs text-gray-500">
          <span>Line {(configuration.code?.substring(0, configuration.code.length).split('\n').length || 1)}</span>
          <span>{configuration.code?.length || 0} characters</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Timeout (seconds)
          </label>
          <input
            type="number"
            value={configuration.timeout || ''}
            onChange={(e) => updateConfig('timeout', parseInt(e.target.value) || undefined)}
            disabled={isReadOnly}
            placeholder="Default: 30 seconds"
            min="1"
            max="300"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Error Handling
          </label>
          <select
            value={configuration.errorHandling || 'stop'}
            onChange={(e) => updateConfig('errorHandling', e.target.value)}
            disabled={isReadOnly}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
          >
            <option value="stop">Stop Workflow</option>
            <option value="continue">Continue</option>
            <option value="retry">Retry (3 attempts)</option>
          </select>
        </div>
      </div>

      <div className="bg-blue-50 p-4 rounded-md space-y-2">
        <h4 className="text-sm font-semibold text-blue-900">Available Objects & Methods:</h4>
        
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div>
            <p className="font-semibold text-blue-800 mb-1">Context Object:</p>
            <ul className="text-blue-700 space-y-0.5 ml-2">
              <li>• context.workflowId</li>
              <li>• context.currentStep</li>
              <li>• context.variables</li>
              <li>• context.user</li>
            </ul>
          </div>
          
          <div>
            <p className="font-semibold text-blue-800 mb-1">Input/Output:</p>
            <ul className="text-blue-700 space-y-0.5 ml-2">
              <li>• input.data</li>
              <li>• input.get(key)</li>
              <li>• output.set(key, value)</li>
              <li>• output.data</li>
            </ul>
          </div>
        </div>
        
        <div className="text-xs">
          <p className="font-semibold text-blue-800 mb-1">Logger Methods:</p>
          <ul className="text-blue-700 space-y-0.5 ml-2 grid grid-cols-4">
            <li>• logger.info()</li>
            <li>• logger.warn()</li>
            <li>• logger.error()</li>
            <li>• logger.debug()</li>
          </ul>
        </div>
        
        <div className="text-xs">
          <p className="font-semibold text-blue-800 mb-1">Utility Functions:</p>
          <ul className="text-blue-700 space-y-0.5 ml-2 grid grid-cols-3">
            <li>• api.get(url)</li>
            <li>• api.post(url, data)</li>
            <li>• utils.uuid()</li>
          </ul>
        </div>
      </div>

      <div className="bg-yellow-50 p-3 rounded-md">
        <p className="text-xs text-yellow-700">
          <strong>Security Note:</strong> Scripts run in a sandboxed environment with limited access.
          Network calls are restricted to approved endpoints only.
        </p>
      </div>

      <div className="text-xs text-gray-500">
        <p className="font-semibold mb-1">Editor Tips:</p>
        <ul className="space-y-0.5">
          <li>• Press <kbd>Tab</kbd> to insert 2 spaces</li>
          <li>• Line numbers shown on the left</li>
          <li>• Character count shown at bottom</li>
          <li>• Use "Load Template" for starter code</li>
          <li>• Use "Trim Whitespace" to clean up code</li>
        </ul>
      </div>
    </div>
  );
};

export default ScriptConfig;